import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import departmentService from '../../services/departmentService';
import './DepartmentPages.css';

const DepartmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [department, setDepartment] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDepartment();
  }, [id]);

  const fetchDepartment = async () => {
    try {
      const data = await departmentService.getDepartmentById(id);
      setDepartment(data.department);
      setStats(data.stats);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch department');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${department.name}"?`)) {
      return;
    }

    try {
      await departmentService.deleteDepartment(id);
      navigate('/app/departments');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete department');
    }
  };

  if (loading) {
    return <div className="loading">Loading department details...</div>;
  }

  if (error) {
    return (
      <div className="department-page">
        <div className="alert alert-danger">{error}</div>
        <button onClick={() => navigate('/app/departments')} className="btn btn-primary">
          Back to Departments
        </button>
      </div>
    );
  }

  const budgetUtilization = departmentService.calculateBudgetUtilization(
    department.budget_allocated,
    department.budget_spent
  );
  const budgetStatus = departmentService.getBudgetStatus(budgetUtilization);

  return (
    <div className="department-detail-page">
      <div className="page-header">
        <button onClick={() => navigate('/app/departments')} className="back-button">
          <i className="fas fa-arrow-left"></i> Back to Departments
        </button>
        <div className="header-actions">
          <Link to={`/app/departments/${id}/edit`} className="btn btn-primary">
            <i className="fas fa-edit"></i> Edit
          </Link>
          <button onClick={handleDelete} className="btn btn-danger">
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      </div>

      <div className="detail-header">
        <div>
          <h1>{department.name}</h1>
          {department.code && <span className="department-code">{department.code}</span>}
        </div>
        <span className={`badge ${department.is_active ? 'badge-success' : 'badge-secondary'}`}>
          {department.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      {department.description && (
        <div className="detail-section">
          <p className="description">{department.description}</p>
        </div>
      )}

  <div className="grid grid-cols-1 gap-5 mb-8 sm:grid-cols-2 xl:grid-cols-4">
  {/* Team Members */}
  <div className="flex items-center p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-blue-100">
      <i className="fas fa-users text-2xl text-blue-600"></i>
    </div>

    <div className="ml-4">
      <p className="text-sm font-medium text-gray-500">Team Members</p>
      <h3 className="text-2xl font-bold text-gray-800">
        {stats?.team_size || 0}
      </h3>
    </div>
  </div>

  {/* Sub Departments */}
  <div className="flex items-center p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-center w-14 h-14 rounded-xl badge-success">
      <i className="fas fa-building text-2xl "></i>
    </div>

    <div className="ml-4">
      <p className="text-sm font-medium text-gray-500">Sub-Departments</p>
      <h3 className="text-2xl font-bold text-gray-800">
        {stats?.sub_departments_count || 0}
      </h3>
    </div>
  </div>

  {/* Projects */}
  <div className="flex items-center p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-center w-14 h-14 rounded-xl badge-warning">
      <i className="fas fa-project-diagram text-2xl"></i>
    </div>

    <div className="ml-4">
      <p className="text-sm font-medium text-gray-500">Projects</p>
      <h3 className="text-2xl font-bold text-gray-800">
        {stats?.projects_count || 0}
      </h3>
    </div>
  </div>

  {/* Budget Used */}
  <div className="flex items-center p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-pink-100">
      <i className="fas fa-chart-line text-2xl text-pink-600"></i>
    </div>

    <div className="ml-4">
      <p className="text-sm font-medium text-gray-500">Budget Used</p>
      <h3 className="text-2xl font-bold text-gray-800">
        {budgetUtilization}%
      </h3>
    </div>
  </div>
</div>

      <div className="detail-grid">
        <div className="detail-section">
          <h2>Department Information</h2>
          <div className="info-grid">
            {department.manager && (
              <div className="info-item">
                <label>Manager</label>
                <div>
                  {department.manager.first_name} {department.manager.last_name}
                  <br />
                  <small>{department.manager.email}</small>
                </div>
              </div>
            )}

            {department.parent_department && (
              <div className="info-item">
                <label>Parent Department</label>
                <Link to={`/app/departments/${department.parent_department.id}`}>
                  {department.parent_department.name}
                </Link>
              </div>
            )}
          </div>
        </div>

        {department.budget_allocated > 0 && (
          <div className="detail-section">
            <h2>Budget Overview</h2>
            <div className="budget-overview">
              <div className="budget-header">
                <span>Budget Utilization</span>
                <span className={`budget-percentage budget-${budgetStatus}`}>
                  {budgetUtilization}%
                </span>
              </div>
              <div className="budget-bar">
                <div 
                  className={`budget-fill budget-${budgetStatus}`}
                  style={{ width: `${Math.min(budgetUtilization, 100)}%` }}
                />
              </div>
              <div className="budget-details">
                <div className="budget-item">
                  <label>Allocated</label>
                  <div className="budget-value">
                    {departmentService.formatBudget(department.budget_allocated)}
                  </div>
                </div>
                <div className="budget-item">
                  <label>Spent</label>
                  <div className="budget-value">
                    {departmentService.formatBudget(department.budget_spent)}
                  </div>
                </div>
                <div className="budget-item">
                  <label>Remaining</label>
                  <div className="budget-value">
                    {departmentService.formatBudget(stats?.budget_remaining || 0)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {department.sub_departments && department.sub_departments.length > 0 && (
        <div className="detail-section">
          <h2>Sub-Departments ({department.sub_departments.length})</h2>
          <div className="sub-departments-list">
            {department.sub_departments.map(subDept => (
              <Link 
                key={subDept.id} 
                to={`/app/departments/${subDept.id}`}
                className="sub-department-card"
              >
                <div>
                  <h4>{subDept.name}</h4>
                  {subDept.manager && (
                    <small>
                      Manager: {subDept.manager.first_name} {subDept.manager.last_name}
                    </small>
                  )}
                </div>
                <i className="fas fa-chevron-right"></i>
              </Link>
            ))}
          </div>
        </div>
      )}

      {department.employees && department.employees.length > 0 && (
        <div className="detail-section">
          <h2>Team Members ({department.employees.length})</h2>
          <div className="team-members-grid">
            {department.employees.map(employee => (
              <div key={employee.id} className="team-member-card">
                <div className="member-avatar">
                  {employee.avatar_url ? (
                    <img src={employee.avatar_url} alt={employee.first_name} />
                  ) : (
                    <i className="fas fa-user"></i>
                  )}
                </div>
                <div className="member-info">
                  <h4>{employee.first_name} {employee.last_name}</h4>
                  <p>{employee.email}</p>
                  <span className={`badge ${employee.is_active ? 'badge-success' : 'badge-secondary'}`}>
                    {employee.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentDetailPage;


